import { EventEmitter, Injectable, Output } from '@angular/core';

import { environment } from '../../environments/environment';
import { DB2DataSourceAvailability, PrismDataSource } from '../models/prism-data-source.model';
import { ReconApiService } from './recon-api.service';

/** Provides global filtering information. */
@Injectable()
export class GlobalFilterService {
  public allDataSources: { [client: string]: PrismDataSource[] };
  private selectedEnvironmentIndex = 0;
  public userDismissedUnavailableEnvironmentDialog = false;

  /** The 4-digit client ID per Auth0  */
  private _selectedClientId: string;
  public get selectedClientId(): string {
    return this._selectedClientId;
  }
  public set selectedClientId(clientId: string) {
    this._selectedClientId = clientId;

    const firstAvailableDataSource = this.firstAvailableDataSourceForClient(this.clientEnvironments);
    if (firstAvailableDataSource === -1) {
      this.selectedEnvironmentIndex = 0;
      this.selectedEnvironmentIsAvailable = false;
    } else {
      this.selectedEnvironmentIndex = firstAvailableDataSource;
      this.selectedEnvironmentIsAvailable = true;
    }
    // If a new client was selected reset the unavailable environment flag that tracks when user dismissed it.
    this.userDismissedUnavailableEnvironmentDialog = false;
  }

  private _selectedEnvironmentId: string;

  public get selectedEnvironmentId(): string {
    return this._selectedEnvironmentId;
  }

  public set selectedEnvironmentId(envId: string) {
    this._selectedEnvironmentId = envId;
  }

  /** Represents the currently filtered environment. */
  public get selectedEnvironment(): PrismDataSource {
    if (this.clientEnvironments == null) {
      return null;
    }
    return this.clientEnvironments[this.selectedEnvironmentIndex];
  }

  public set selectedEnvironment(prismDataSource: PrismDataSource) {
    for (let i = 0; i < this.clientEnvironments.length; i++) {
      if (prismDataSource !== this.clientEnvironments[i]) {
        continue;
      }
      this.selectedEnvironmentIndex = i;
      break;
    }
    // We keep track of if the selected environment is available or not, so does the polling of the API.
    // this is in the event that a a user has the first of two environemnts for a client selected, it goes down, they select the other.
    this.selectedEnvironmentIsAvailable = prismDataSource.DataSourceStatus === DB2DataSourceAvailability.AVAILABLE;
    // If a new client was selected reset the unavailable environment flag that tracks when user dismissed it.
    this.userDismissedUnavailableEnvironmentDialog = false;
  }

  /** Represents a client's environment, as well as any child environments. */
  public get clientEnvironments(): PrismDataSource[] {
    return this.allDataSources == null
      ? null
      : this.allDataSources[parseInt(this.selectedClientId, 10)];
  }

  private _selectedEnvironmentIsAvailable = true;
  public get selectedEnvironmentIsAvailable() {
    return this._selectedEnvironmentIsAvailable;
  }
  public set selectedEnvironmentIsAvailable(newValue: boolean) {
    if (newValue === true) {
      this.userDismissedUnavailableEnvironmentDialog = false;
    }
    this._selectedEnvironmentIsAvailable = newValue;
  }

  @Output() filtersLoaded: EventEmitter<boolean> = new EventEmitter();

  @Output() clientDataSourceSelectionChanged: EventEmitter<PrismDataSource> = new EventEmitter();
  @Output() clientEnvDataSourceSelectionChanged: EventEmitter<PrismDataSource> = new EventEmitter();

  constructor(
    private reconApiService: ReconApiService,
  ) {
    this.reconApiService.getPrismDataSources().then((result) => this.onReceivedPrismDataSources(result));
    setInterval(() => {
      this.reconApiService.getPrismDataSources().then((result) => this.onReceivedPrismDataSources(result));
    }, environment.globalFilterRefreshInterval);
  }

  private onReceivedPrismDataSources(result: { [client: string]: PrismDataSource[] }) {

    this.allDataSources = result;
    for (const key in this.allDataSources) {
      if (!key) {
        continue;
      }

      // If this is the initial page load then we need to determine
      // who the first client with an available datasource is.
      if (this.selectedClientId === undefined) {

        const firstAvailableDataSource = this.firstAvailableDataSourceForClient(this.allDataSources[key]);

        // If no data sources for this client are available we'll move to the next one.
        if (firstAvailableDataSource === -1) {
          continue;
        }

        this.selectedClientId = this.allDataSources[key][0].ClientId4Digit;
        this.selectedEnvironmentId = this.allDataSources[key][0].ClientId3Digit;
        this.selectedEnvironmentIndex = firstAvailableDataSource;
        break;
      }
    }

    // If we reach the end of the loop and we still don't have a selected client we
    if (this.selectedClientId === undefined) {
      // will loop again to select the first one in the list that is unavailable.
      for (const key in this.allDataSources) {
        if (!key) {
          continue;
        }
        this.selectedClientId = this.allDataSources[key][0].ClientId4Digit;
        this.selectedEnvironmentId = this.allDataSources[key][0].ClientId3Digit;
        this.selectedEnvironmentIndex = 0;
        break;
      }
    }

    // In the event that our currently selected environment goes down during one of the
    // updates of the alldatasources then we need to display a message to the user
    // indicating that their datasource is no longer available.
    if (this.selectedEnvironment && this.selectedEnvironment.DataSourceStatus !== DB2DataSourceAvailability.AVAILABLE) {
      this.selectedEnvironmentIsAvailable = false;
    } else {
      this.selectedEnvironmentIsAvailable = true;
    }

    this.filtersLoaded.emit(true);
  }

  private firstAvailableDataSourceForClient(clientDataSources: PrismDataSource[]): number {
    // If we select a client with multiple datasources then return the first datasource that is available.
    // or -1 if none are found.
    if (clientDataSources) {
      for (let i = 0; i < clientDataSources.length; i++) {
        if (clientDataSources[i].DataSourceStatus === 0) {
          return i;
        }
      }
    }
    return -1;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/global-filter.service.ts